// ========== React & Libraries ==========
import { useMemo } from 'react';
import { z } from 'zod';
import { Pencil } from 'lucide-react';

import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Temporal } from 'temporal-polyfill';

// ========== Commons ==========
import { formatInstant, FrequentTimeZones, isAirAdmin } from '@airreading/common';
import {
  CoachGoogleMeetTqReview,
  GetCoachesGoogleMeetTqReviewSortableColumn,
  getCoachesGoogleMeetTqReviewSortableColumnSchema,
  getCoachesGoogleMeetTqReviewSortOrderSchema,
  googleMeetBlankEvaluationStatus,
  GoogleMeetEvaluationStatusLabelMap,
  googleMeetEvaluationStatusFilterSchema,
  googleMeetEvaluationStatusSchema,
} from '@airreading/common/data';

// ========== Utils & Helpers ==========
import { getCoachesGoogleMeetTqReview, EmptyArray } from '@/lib/api-calls';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing';
import { usePaginatedFilters } from '@/lib/filtering';
import { useErrorToast } from '@/lib/error-handling';
import { paginationRequestSchema } from '@/lib/pagination';
import { airColumnHelper } from '@/lib/tables';
import { useSorting } from '@/lib/sorting';

// ========== Table Components ==========
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';

// ========== Layout Components ==========
import { InfoPage } from '@/components/info-page';

// ========== Local Components ==========
import { formatStudentClassTimeAbbr } from '@/routes/_layout/scheduling/schedules/-components/formatting';
import { coachesSectionTabs } from '../-components/tabs';
import { EditGoogleMeetTqReview } from '../-components/edit-google-meet-tq-review';
import { GoogleMeetEvaluationStatusColorMap } from '../-components/google-meet-evaluation-status';

const searchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  evaluationStatuses: z.array(googleMeetEvaluationStatusFilterSchema).optional(),
  sortBy: getCoachesGoogleMeetTqReviewSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/teachers/google-meet-tq-review/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: GoogleMeetTqReviewIndex,
  validateSearch: searchSchema,
});

const columnHelper = airColumnHelper<CoachGoogleMeetTqReview>(getCoachesGoogleMeetTqReviewSortableColumnSchema);

// The query returns meeting-block times as plain-time strings; hydrate them into
// Temporal.PlainTime so we can reuse `formatStudentClassTimeAbbr` for the grouping.
function formatClassTime(classTime: NonNullable<CoachGoogleMeetTqReview['nextGoogleMeetSession']>['classTime']) {
  return formatStudentClassTimeAbbr({
    type: classTime.type,
    meetingBlocks: classTime.meetingBlocks.map((block) => ({
      day: block.day,
      startPlainTime: Temporal.PlainTime.from(block.startPlainTime),
      endPlainTime: Temporal.PlainTime.from(block.endPlainTime),
    })),
  });
}

const tableColumns = [
  columnHelper.display({
    id: 'edit',
    header: '',
    cell: ({ row }) => (
      <EditGoogleMeetTqReview
        coach={row.original}
        trigger={
          <button className="p-1 hover:text-primary" aria-label="Edit Google Meet TQ review" title="Edit">
            <Pencil size={16} />
          </button>
        }
      />
    ),
    size: 40,
  }),

  columnHelper.accessor('fullNameOrEmail', {
    id: 'fullNameOrEmail',
    header: 'Name',
    cell: ({ row, getValue }) => (
      <Link to="/teachers/$teacherId" params={{ teacherId: row.original.id }}>
        {getValue()}
      </Link>
    ),
  }),

  columnHelper.accessor((row) => row.nextGoogleMeetSession, {
    id: 'nextGoogleMeetSessionDate',
    header: 'Next GM class',
    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) return null;
      return formatInstant(session.startInstant, FrequentTimeZones.UsCentral).formattedDate;
    },
  }),

  columnHelper.accessor((row) => row.nextGoogleMeetSession?.classTime, {
    id: 'classTime',
    header: 'Class time',
    cell: ({ getValue }) => {
      const classTime = getValue();
      if (!classTime) return null;
      return formatClassTime(classTime);
    },
  }),

  columnHelper.accessor('googleMeetNotes', {
    id: 'googleMeetNotes',
    header: 'Google Meet notes',
    cell: ({ getValue }) => {
      const notes = getValue();
      if (!notes) return '–';
      return <div className="max-w-64 line-clamp-2">{notes}</div>;
    },
  }),

  columnHelper.accessor('googleMeetEvaluationStatus', {
    id: 'googleMeetEvaluationStatus',
    header: 'Evaluation status',
    cell: ({ getValue }) => {
      const status = getValue();
      if (!status) return '–';
      return (
        <span className={GoogleMeetEvaluationStatusColorMap[status]}>{GoogleMeetEvaluationStatusLabelMap[status]}</span>
      );
    },
  }),

  columnHelper.accessor('JPSGoogleMeetUrl', {
    id: 'JPSGoogleMeetUrl',
    header: 'JPS Google Meet link',
    cell: ({ getValue }) => {
      const url = getValue();
      if (!url) return '–';
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary underline">
          {url}
        </a>
      );
    },
  }),

  columnHelper.accessor('airReadingGoogleMeetUrl', {
    id: 'airReadingGoogleMeetUrl',
    header: 'Air Google Meet link',
    cell: ({ getValue }) => {
      const url = getValue();
      if (!url) return '–';
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary underline">
          {url}
        </a>
      );
    },
  }),
];

function GoogleMeetTqReviewIndex() {
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[nameQuery, activeNameQuery], setNameQuery],
      evaluationStatuses: [[evaluationStatuses, activeEvaluationStatuses], setEvaluationStatuses],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'evaluationStatuses']);

  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetCoachesGoogleMeetTqReviewSortableColumn>([
    sortBy,
    didChangeSortBy,
  ]);

  const { data, isPending, error, refetch } = useQuery(
    getCoachesGoogleMeetTqReview({
      pageIndex,
      pageSize,
      filters: {
        query: activeNameQuery,
        evaluationStatuses: activeEvaluationStatuses,
      },
      sortBy,
    }),
  );

  const coaches = useMemo(() => data?.coaches ?? EmptyArray, [data]);

  useErrorToast(error, 'Error loading Google Meet TQ review');

  const table = useReactTable({
    data: coaches,
    columns: tableColumns,
    state: {
      pagination: { pageIndex, pageSize },
      sorting,
    },
    getRowId: (row) => row.id,
    rowCount: data?.total ?? 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    ...tableSortingOptions,
  });

  const evaluationStatusOptions = [
    { label: 'Blank', value: googleMeetBlankEvaluationStatus },
    ...googleMeetEvaluationStatusSchema.options.map((value) => ({
      label: GoogleMeetEvaluationStatusLabelMap[value],
      value,
      className: GoogleMeetEvaluationStatusColorMap[value],
    })),
  ];

  const filterComponents = (
    <>
      <TableFilterInputField
        placeholder="Search name or email"
        value={nameQuery ?? ''}
        onChange={(e) => setNameQuery(e.target.value)}
      />
      <TableFilterDropdown
        title="Evaluation status"
        options={evaluationStatusOptions}
        selectedValues={evaluationStatuses ?? []}
        onChange={setEvaluationStatuses}
        data-testid="google-meet-tq-review-filter-dropdown-evaluation-status"
      />
      <TableFilterResetButton show={true} onClick={resetFilters} />
    </>
  );

  return (
    <InfoPage headerText="Teachers" tabs={coachesSectionTabs()}>
      <EntityTable
        table={table}
        showPagination
        isLoading={isPending}
        hasError={!!error}
        emptyState="No teachers with Google Meet sessions found"
        autoManagedColumnIds={['edit']}
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents: filterComponents }}
      />
    </InfoPage>
  );
}
